import { CgArrowsExchangeAltV } from "react-icons/cg";
import { statCore } from "./core/stat";
import { defineWidget } from "../widgetDefinition";
import { statSlots } from "../Slots";
import { statDuplicate } from "../Duplication/stat";
import { statControlPanel } from "../ControlPanel/stat";
import type { StatComponentSpec } from "../Processing/types";
import { StatTile } from "../WidgetComponents/StatTile";
import { StatInfo } from "../Info/stat";
import { StatConfigurator } from "../AddWidgetModal/StatConfigurator";
import { BINDABLE_SECTIONS } from "../Processing/controllerBindings";

export const statDefinition = defineWidget<StatComponentSpec>({
  ...statCore,
  type: "stat_tile",
  dynamic: { bindableSections: BINDABLE_SECTIONS.stat_tile },
  slots: statSlots,
  duplicate: statDuplicate,
  controlPanel: statControlPanel,
  info: { Content: StatInfo },
  schema: ["display", "queryId", "stat_tile_builder_misc"],
  contractHash: "029u73ad04c",
  needsData: true,

  template: {
    label: "Stat Tile",
    description: "Single metric summary card",
    icon: <CgArrowsExchangeAltV />,
  },

  defaultLayout: { w: 3, h: 3, minW: 2, minH: 3 },

  gridComponent: ({ id }) => <StatTile id={id} />,

  configurator: {
    modalTitle: "Stat Tile",
    wide: true,
    Component: StatConfigurator as React.ComponentType<any>,
  },

  pdf: {
    buildSnapshot({ processed }) {
      if (processed.type !== "stat_tile") return null;
      const ms = processed.stat.metricStat;
      const label = processed.stat.label || processed.spec.display.title || "—";
      const metricAgg = processed.spec.stat_tile_builder_misc?.metricAgg ?? "avg";

      // The backend reports the decimal count the admin panel configured for the metric.
      const numDecimals = Number.isInteger(ms?.num_decimals) ? (ms!.num_decimals as number) : null;
      const fmtVal = (v: number | string | null | undefined): string | null => {
        if (v == null) return null;
        const n = Number(v);
        if (isNaN(n)) return String(v);
        return numDecimals != null ? n.toFixed(numDecimals) : String(Math.round((n + Number.EPSILON) * 100) / 100);
      };

      const showTeamPosData = processed.spec.stat_tile_builder_misc?.showTeamPosData !== false;

      if (!ms) {
        return { type: "stat_tile", label, metricAgg, latestDate: null, isAsym: false, latestValue: null, diff: null, lowGood: false, left: null, right: null, team: null, pos: null, showTeamPosData, numDecimals: null };
      }

      return {
        type: "stat_tile",
        label: label || ms.name || "—",
        metricAgg,
        latestDate: ms.latest_date
          ? `Latest date: ${new Date(ms.latest_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
          : null,
        isAsym: ms.is_asym,
        latestValue: fmtVal(ms.latest_value),
        diff: ms.diff,
        lowGood: ms.low_good ?? null,
        left: ms.left ? { value: fmtVal(ms.left.latest_value) ?? "—", diff: ms.left.diff } : null,
        right: ms.right ? { value: fmtVal(ms.right.latest_value) ?? "—", diff: ms.right.diff } : null,
        team: showTeamPosData ? ms.team : null,
        pos: showTeamPosData ? ms.pos : null,
        showTeamPosData,
        numDecimals,
      };
    },
    render({ snapshot, rect, scale }) {
      if (snapshot.type !== "stat_tile") return null;
      return renderStat(snapshot, rect, scale);
    },
  },
});
